<template>
  <div class="w-full h-full overflow-y-auto custom-scrollbar bg-dark-secondary" style="scrollbar-gutter: stable;">
    <div class="max-w-[900px] mx-auto w-full px-6 lg:px-8 py-6 lg:py-8 flex flex-col gap-6">

      <div class="mb-2">
        <h1 class="text-2xl font-bold text-dark-primary tracking-tight">Settings</h1>
        <p class="text-sm text-dark-secondary mt-1">Manage your account, team and workspace configuration.</p>
      </div>

      <section class="settings-section !p-0 overflow-hidden">
        <!-- Section header -->
        <div class="flex items-center gap-3 px-6 py-4 border-b theme-border">
          <div class="settings-section-icon"><UserCircle class="w-4 h-4" /></div>
          <div>
            <h2 class="settings-section-title">Account & Team</h2>
            <p class="settings-section-desc">Your identity and workspace members.</p>
          </div>
        </div>

        <!-- Your account -->
        <div class="border-b theme-border last:border-0">
          <button
            @click="accountOpenSection = accountOpenSection === 'account' ? null : 'account'"
            class="w-full flex items-center gap-3 pl-10 pr-6 py-3.5 hover:bg-dark-tertiary/50 transition text-left"
          >
            <div class="settings-accordion-icon"><UserCircle class="w-4 h-4" /></div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-dark-primary">Your account</p>
              <p class="text-xs text-dark-secondary mt-0.5 truncate">{{ authStore.user?.email }}</p>
            </div>
            <span class="text-[10px] font-semibold uppercase tracking-wider text-dark-tertiary mr-2">{{ authStore.role ?? 'no role' }}</span>
            <ChevronDown class="w-4 h-4 text-dark-tertiary transition-transform duration-200 flex-shrink-0" :class="{ 'rotate-180': accountOpenSection === 'account' }" />
          </button>
          <div v-if="accountOpenSection === 'account'" class="px-6 pb-5 pt-1">
            <div class="flex items-center gap-4 p-4 bg-dark-tertiary rounded-xl border theme-border">
              <div class="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden" style="background: rgba(var(--accent-rgb), 0.2);">
                <img v-if="authStore.user?.photoURL" :src="authStore.user.photoURL" :alt="authStore.user.displayName" class="w-full h-full object-cover" @error="$event.target.style.display='none'" />
                <div class="w-full h-full flex items-center justify-center">
                  <span class="text-sm font-bold" style="color: var(--accent);">{{ userInitial }}</span>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-dark-primary truncate">{{ authStore.user?.email }}</p>
                <p class="text-xs text-dark-tertiary mt-0.5 font-mono">{{ authStore.tenantId ?? 'No tenant' }}</p>
              </div>
              <button @click="showDeleteAccount = true" class="p-1.5 text-dark-secondary hover:text-error transition rounded" title="Delete account">
                <Trash2 class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <template v-if="authStore.isAdmin && authStore.hasTenant">
          <!-- Team members -->
          <!-- Team & Invites -->
          <div class="last:border-0">
            <button
              @click="accountOpenSection = accountOpenSection === 'team' ? null : 'team'"
              class="w-full flex items-center gap-3 pl-10 pr-6 py-3.5 hover:bg-dark-tertiary/50 transition text-left"
            >
              <div class="settings-accordion-icon"><Users class="w-4 h-4" /></div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-dark-primary">Team & Invites</p>
                <p class="text-xs text-dark-secondary mt-0.5 truncate">
                  {{ users.filter(u => u.id !== authStore.user?.uid).length }} member(s)<span v-if="invites.length"> · {{ invites.length }} pending invite(s)</span>
                </p>
              </div>
              <ChevronDown class="w-4 h-4 text-dark-tertiary transition-transform duration-200 flex-shrink-0" :class="{ 'rotate-180': accountOpenSection === 'team' }" />
            </button>
            <div v-if="accountOpenSection === 'team'" class="px-6 pb-5 pt-1 space-y-4">

              <!-- Members -->
              <div class="space-y-2">
                <p class="text-xs font-semibold text-dark-secondary uppercase tracking-wide">Members</p>
                <div v-if="users.filter(u => u.id !== authStore.user?.uid).length > 0" class="space-y-2">
                  <div v-for="u in users.filter(u => u.id !== authStore.user?.uid)" :key="u.id" class="flex items-center gap-3 p-3 bg-dark-tertiary rounded-xl border theme-border">
                    <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style="background: rgba(var(--accent-rgb), 0.1);">
                      <span class="text-xs font-bold" style="color: var(--accent);">{{ (u.email ?? u.id)[0].toUpperCase() }}</span>
                    </div>
                    <div class="flex-1 min-w-0"><p class="text-sm text-dark-primary truncate">{{ u.email ?? u.id }}</p></div>
                    <select :value="u.role" @change="changeRole(u.id, $event.target.value)" class="settings-select">
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <button @click="removeUserConfirm(u)" class="p-1.5 text-dark-secondary hover:text-error transition rounded"><Trash2 class="w-4 h-4" /></button>
                  </div>
                </div>
                <p v-else class="text-sm text-dark-tertiary italic">No other team members yet.</p>
              </div>

              <!-- Pending invites -->
              <div v-if="invites.length" class="space-y-2">
                <p class="text-xs font-semibold text-dark-secondary uppercase tracking-wide">Pending invites</p>
                <div v-for="inv in invites" :key="inv.id" class="flex items-center gap-3 p-3 bg-dark-tertiary/50 rounded-xl border border-dashed theme-border">
                  <Mail class="w-4 h-4 text-dark-secondary flex-shrink-0" />
                  <p class="flex-1 text-sm text-dark-secondary truncate">{{ inv.email }}</p>
                  <span class="text-[10px] font-semibold uppercase tracking-wider text-dark-tertiary">{{ inv.role }}</span>
                  <button @click="revokeInviteConfirm(inv)" class="p-1.5 text-dark-secondary hover:text-error transition rounded"><X class="w-4 h-4" /></button>
                </div>
              </div>

              <!-- Invite form -->
              <div class="p-4 bg-dark-tertiary/30 rounded-xl border border-dashed theme-border space-y-3">
                <p class="text-xs font-semibold text-dark-secondary uppercase tracking-wide">Invite a team member</p>
                <div class="flex gap-2">
                  <input v-model="inviteEmail" type="email" placeholder="colleague@company.com" class="settings-input flex-1" @keydown.enter="sendInvite" />
                  <select v-model="inviteRole" class="settings-select w-36">
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button @click="sendInvite" :disabled="!inviteEmail || inviting" class="px-4 py-2 bg-accent text-white rounded-lg text-sm font-semibold hover:opacity-90 transition disabled:opacity-50">
                    <Loader2 v-if="inviting" class="w-4 h-4 animate-spin" /><span v-else>Invite</span>
                  </button>
                </div>
                <p v-if="inviteSuccess" class="text-xs text-success">Invite sent to {{ inviteSuccess }}</p>
                <p v-if="inviteError" class="text-xs text-error">{{ inviteError }}</p>
              </div>

            </div>
          </div>
        </template>
      </section>

      <section class="settings-section !p-0 overflow-hidden">
        <div class="flex items-center gap-3 px-6 py-4 border-b theme-border">
          <div class="settings-section-icon flex-shrink-0"><Server class="w-4 h-4" /></div>
          <div class="flex-1">
            <h2 class="settings-section-title">Workspaces</h2>
            <p class="settings-section-desc">Manage your environments, backend provisioning, and API configurations.</p>
          </div>
          <button
            v-if="authStore.isAdmin"
            @click="createWorkspace"
            :disabled="creatingWorkspace || (authStore.ownedTenantIds?.length ?? 0) >= 3 || !workspaceConfigs[authStore.tenantId]?.apiBase || !workspaceConfigs[authStore.tenantId]?.apiKey"
            class="w-7 h-7 flex items-center justify-center rounded-md text-dark-tertiary hover:text-dark-primary hover:bg-dark-tertiary transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
            :title="(authStore.ownedTenantIds?.length ?? 0) >= 3 ? 'Maximum of 3 workspaces reached' : (!workspaceConfigs[authStore.tenantId]?.apiBase || !workspaceConfigs[authStore.tenantId]?.apiKey) ? 'Configure your current workspace before creating another' : 'New workspace'"
          >
            <Loader2 v-if="creatingWorkspace" class="w-3.5 h-3.5 animate-spin" />
            <span v-else class="text-base leading-none">+</span>
          </button>
        </div>

        <div>
          
          <div v-for="inv in pendingReceivedInvites" :key="inv.id" class="flex items-center justify-between pl-10 pr-6 py-3 border-b theme-border bg-warning/5">
             <div class="flex items-center gap-3">
               <Mail class="w-4 h-4 text-warning flex-shrink-0" />
               <span class="text-sm font-semibold text-dark-primary font-mono">{{ inv.tenantId }}</span>
               <span class="text-xs text-dark-secondary">Invite to join as <span class="text-warning font-semibold">{{ inv.role }}</span></span>
             </div>
             <div class="flex gap-2">
               <button @click="handleDeclineInvite(inv)" class="px-3 py-1.5 bg-dark-tertiary text-dark-primary rounded-lg text-xs font-medium hover:bg-dark-quaternary transition">Decline</button>
               <button @click="handleAcceptInvite(inv)" class="px-3 py-1.5 bg-warning text-dark-primary rounded-lg text-xs font-bold hover:opacity-90 transition">Accept</button>
             </div>
          </div>

          <div
            v-for="t in allWorkspaces" :key="t.tenantId"
            class="border-b theme-border last:border-0"
          >
            <div class="flex items-center pl-10 pr-6 py-3.5 gap-3">
              
              <div class="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <button
                  @click="switchWorkspace(t.tenantId)"
                  :disabled="t.tenantId === authStore.tenantId || !!switchingTenant"
                  class="w-2.5 h-2.5 rounded-full transition-all flex-shrink-0 cursor-pointer hover:scale-125 disabled:cursor-default disabled:scale-100"
                  :class="t.tenantId === authStore.tenantId ? 'bg-accent' : 'border-2'"
                  :style="t.tenantId !== authStore.tenantId ? 'border-color: var(--text-tertiary); opacity: 0.45' : ''"
                  :title="t.tenantId === authStore.tenantId ? 'Active workspace' : 'Switch to this workspace'"
                >
                  <Loader2 v-if="switchingTenant === t.tenantId" class="w-2.5 h-2.5 animate-spin text-accent" />
                </button>
              </div>
              
              <div class="flex-1 min-w-0">
                <p class="text-sm font-bold text-dark-primary font-mono">{{ t.tenantId }}</p>
                <p class="text-xs font-mono mt-0.5 truncate"
                  :class="workspaceConfigs[t.tenantId]?.isConnected ? 'text-dark-secondary' : 'text-dark-tertiary italic opacity-70'">
                  {{ workspaceConfigs[t.tenantId]?.apiBase || 'No API configured' }}
                </p>
              </div>

              <div class="flex items-center gap-2 flex-shrink-0">
                <span class="text-[10px] font-semibold uppercase tracking-wider text-dark-tertiary">{{ t.role }}</span>
              </div>

              <div class="w-px h-4 theme-bg-border mx-0.5 flex-shrink-0"></div>

              <div class="flex items-center gap-0.5 flex-shrink-0">
                <button @click="testConnection(t.tenantId)" class="ws-icon-btn" title="Test Connection"
                  :disabled="!workspaceConfigs[t.tenantId]?.apiBase && !editingWorkspace[t.tenantId]"
                  :class="!workspaceConfigs[t.tenantId]?.apiBase && !editingWorkspace[t.tenantId] ? 'opacity-30 cursor-not-allowed' : ''">
                  <Loader2 v-if="testingConnection[t.tenantId]" class="w-4 h-4 animate-spin text-accent" />
                  <Check v-else-if="testResult[t.tenantId] === 'ok'" class="w-4 h-4 text-success" />
                  <X v-else-if="testResult[t.tenantId] === 'error'" class="w-4 h-4 text-error" />
                  <Activity v-else class="w-4 h-4" />
                </button>

                <button v-if="workspaceConfigs[t.tenantId]?.isConnected" @click="disconnectConfirm(t.tenantId)" class="ws-icon-btn ws-icon-btn-danger" title="Disconnect backend">
                  <Unlink class="w-4 h-4" />
                </button>
                <button v-else @click="reconnectWorkspace(t.tenantId)" class="ws-icon-btn" title="Connect backend">
                  <Link class="w-4 h-4" />
                </button>

                <button v-if="authStore.ownedTenantIds?.includes(t.tenantId) || workspaceConfigs[t.tenantId]?.isSoleAdmin" @click="deleteWorkspaceConfirm(t.tenantId)" class="ws-icon-btn ws-icon-btn-danger" title="Delete Workspace">
                  <Trash2 class="w-4 h-4" />
                </button>
                <button v-else @click="leaveWorkspaceConfirm(t)" class="ws-icon-btn ws-icon-btn-danger" title="Leave Workspace">
                  <LogOut class="w-4 h-4" />
                </button>
              </div>

              <div class="w-px h-4 theme-bg-border mx-0.5 flex-shrink-0"></div>

              <button @click="toggleEdit(t.tenantId)" class="ws-icon-btn transition-colors" title="Configure API"
                :class="editingWorkspace[t.tenantId] ? 'bg-dark-quaternary text-accent' : ''">
                <ChevronDown class="w-4 h-4 transition-transform duration-200" :class="{ 'rotate-180': editingWorkspace[t.tenantId] }" />
              </button>

            </div>

            <div
              v-if="editingWorkspace[t.tenantId] && workspaceConfigs[t.tenantId]"
              class="px-6 pb-5 pt-4"
              style="background: rgba(var(--accent-rgb), 0.02);"
            >
              <div class="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label class="settings-label">API Base URL</label>
                  <input v-model="workspaceConfigs[t.tenantId].editBase" type="url" class="settings-input" placeholder="https://your-instance.seguraw.ai" />
                </div>
                <div>
                  <label class="settings-label">API Key</label>
                  <div class="relative">
                    <input
                      v-model="workspaceConfigs[t.tenantId].editKey"
                      :type="workspaceConfigs[t.tenantId].showKey ? 'text' : 'password'"
                      class="settings-input"
                      style="padding-right: 3.5rem !important;"
                      placeholder="Paste API key"
                    />
                    <button @click="workspaceConfigs[t.tenantId].showKey = !workspaceConfigs[t.tenantId].showKey"
                      class="absolute right-3 top-1/2 -translate-y-1/2 text-dark-secondary hover:text-dark-primary transition">
                      <Eye v-if="!workspaceConfigs[t.tenantId].showKey" class="w-4 h-4" />
                      <EyeOff v-else class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <button @click="saveWorkspaceConfig(t.tenantId)" :disabled="savingWorkspace[t.tenantId]" class="w-28 flex items-center justify-center py-2.5 bg-accent text-white rounded-lg text-sm font-semibold hover:opacity-90 transition disabled:opacity-50">
                    <Loader2 v-if="savingWorkspace[t.tenantId]" class="w-4 h-4 animate-spin mr-1.5" />
                    <span v-else>Save</span>
                  </button>
                  <button @click="toggleEdit(t.tenantId)" class="w-28 flex items-center justify-center py-2.5 bg-dark-tertiary text-dark-primary border theme-border rounded-lg text-sm font-medium hover:bg-dark-quaternary transition">
                    Cancel
                  </button>
                  <span v-if="savedWorkspace[t.tenantId]" class="text-xs text-success flex items-center gap-1 ml-2"><Check class="w-3.5 h-3.5" /> Saved</span>
                </div>

                <button v-if="!workspaceConfigs[t.tenantId]?.apiBase && isAllowedOrigin" @click="requestProvisioning(t.tenantId)" :disabled="provisioning[t.tenantId]" class="text-xs text-accent font-semibold hover:underline">
                  <Loader2 v-if="provisioning[t.tenantId]" class="w-3 h-3 animate-spin inline mr-1" />
                  {{ provisioning[t.tenantId] ? 'Requesting' : 'Request' }}
                </button>
              </div>
              
              <p v-if="provisionMsg[t.tenantId]"   class="text-xs text-accent mt-3">{{ provisionMsg[t.tenantId] }}</p>
              <p v-if="provisionError[t.tenantId]" class="text-xs text-error mt-3">{{ provisionError[t.tenantId] }}</p>
            </div>
          </div>
        </div>
      </section>

      <section v-if="(authStore.isAdmin || authStore.isEditor) && authStore.hasTenant" class="settings-section !p-0 overflow-hidden">
        
        <div class="flex items-center gap-3 px-6 py-4 border-b theme-border">
          <div class="settings-section-icon flex-shrink-0"><Sparkles class="w-4 h-4" /></div>
          <div class="flex-1 min-w-0">
            <h2 class="settings-section-title">AI Configuration</h2>
            <p class="settings-section-desc">Configure system prompts, models, and analytical behavior.</p>
          </div>
        </div>

        <div>
          <div v-for="p in PROMPT_SECTIONS" :key="p.key" class="border-b theme-border">
            <button
              @click="toggleAiSection(p.key)"
              class="w-full flex items-center gap-3 pl-10 pr-6 py-3.5 hover:bg-dark-tertiary/50 transition text-left"
            >
              <div class="settings-accordion-icon flex-shrink-0">
                <component :is="p.icon" class="w-4 h-4" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-dark-primary">{{ p.label }}</p>
                <p class="text-xs text-dark-secondary mt-0.5 truncate">
                  <span v-if="promptState[p.key]?.source === 'db'" class="text-accent font-medium">Customized version active</span>
                  <span v-else>Using default prompt</span>
                </p>
              </div>
              <ChevronDown class="w-4 h-4 text-dark-tertiary transition-transform duration-200 flex-shrink-0" :class="{ 'rotate-180': aiOpenSection === p.key }" />
            </button>
            
            <div v-if="aiOpenSection === p.key" class="px-6 pb-5 pt-1">
              <div v-if="promptsLoading[p.key]" class="py-6 text-center flex flex-col items-center gap-2 text-sm text-dark-secondary">
                <Loader2 class="w-5 h-5 animate-spin text-accent" /> Loading configuration...
              </div>
              <template v-else>
                <div class="grid grid-cols-[1fr_240px] gap-6 bg-dark-tertiary/30 rounded-xl border theme-border p-5 items-start">
                  <div class="space-y-4">
                    <div>
                      <div class="flex items-center justify-between mb-1.5">
                        <label class="settings-label !mb-0">Prompt Content</label>
                      </div>
                      <textarea
                        v-model="promptState[p.key].editContent"
                        class="settings-input resize-none font-mono text-xs"
                        style="height: 200px; line-height: 1.6;"
                        :placeholder="promptState[p.key].defaultContent || 'Enter prompt content…'"
                      ></textarea>
                    </div>

                    <div>
                      <label class="settings-label">Note (optional)</label>
                      <input v-model="promptState[p.key].note" type="text" placeholder="Describe what changed…" class="settings-input" />
                    </div>

                    <div class="flex items-center gap-3 pt-1">
                      <button
                        @click="savePromptVersion(p.key)"
                        :disabled="savingPrompt[p.key] || !promptState[p.key].editContent.trim() || promptState[p.key].editContent === promptState[p.key].originalContent"
                        class="w-28 flex items-center justify-center py-2.5 bg-accent text-white rounded-lg text-sm font-semibold hover:opacity-90 transition disabled:opacity-50"
                      >
                        <Loader2 v-if="savingPrompt[p.key]" class="w-4 h-4 animate-spin mr-1.5" />
                        <span v-else>Save</span>
                      </button>
                      <button @click="aiOpenSection = null" class="w-28 flex items-center justify-center py-2.5 bg-dark-tertiary text-dark-primary border theme-border rounded-lg text-sm font-medium hover:bg-dark-quaternary transition">
                        Close
                      </button>
                      <span v-if="savePromptSuccess[p.key]" class="text-xs text-success flex items-center gap-1 ml-2"><Check class="w-3.5 h-3.5" /> Saved</span>
                    </div>
                  </div>

                  <div class="border-l theme-border pl-5 flex flex-col" style="min-height: 100%;">
                    <p class="text-xs font-semibold text-dark-secondary uppercase tracking-wide mb-2">Version History</p>
                    <div class="space-y-1.5 flex-1 overflow-y-auto custom-scrollbar pr-1">
                      
                      <!-- Default (System) -->
                      <div
                        class="flex items-center justify-between px-3 rounded-lg min-h-[44px] transition-all group cursor-pointer"
                        :style="promptState[p.key].source === 'file'
                          ? 'background: rgba(var(--accent-rgb), 0.08)'
                          : 'background: var(--bg-tertiary)'"
                        @click="previewVersion(p.key, null)"
                      >
                        <span class="text-xs font-mono truncate select-none" :class="promptState[p.key].source === 'file' ? 'text-accent font-semibold' : 'text-dark-secondary'">Default (System)</span>
                        <button
                          v-if="promptState[p.key].source === 'db'"
                          @click.stop="resetPromptToDefault(p.key)"
                          class="p-1.5 text-dark-tertiary hover:text-accent transition rounded flex-shrink-0 opacity-0 group-hover:opacity-100" title="Use default"
                        ><Play class="w-3.5 h-3.5" /></button>
                      </div>

                      <!-- Custom versions -->
                      <div
                        v-for="v in promptState[p.key].versions" :key="v.id"
                        class="flex items-center justify-between px-3 min-h-[44px] rounded-lg transition-all group cursor-pointer"
                        :style="v.is_active
                          ? 'background: rgba(var(--accent-rgb), 0.08)'
                          : 'background: var(--bg-tertiary)'"
                        @click="previewVersion(p.key, v.id)"
                      >
                        <div class="flex flex-col min-w-0 pr-2 flex-1 py-2">
                          <input
                            :value="editingNoteId === v.id ? editNoteText : (v.note || 'Unnamed')"
                            :readonly="editingNoteId !== v.id"
                            @dblclick.stop="startEditNote(v); $event.target.focus()"
                            @input="editNoteText = $event.target.value"
                            @blur="saveNote(p.key, v.id)"
                            @keydown.enter.prevent="$event.target.blur()"
                            @keydown.esc.prevent="editNoteText = v.note || ''; $event.target.blur()"
                            class="text-xs font-mono bg-transparent w-full min-w-0 p-0 select-none"
                            :class="[
                              v.is_active ? 'text-accent font-semibold' : 'text-dark-primary',
                              editingNoteId === v.id ? 'cursor-text select-text' : 'cursor-pointer'
                            ]"
                            style="border: none; outline: none; box-shadow: none;"
                          />
                          <p class="text-[9px] mt-0.5 tracking-wide select-none" :class="v.is_active ? 'text-accent/50' : 'text-dark-tertiary'">{{ formatDate(v.created_at) }}</p>
                        </div>
                        
                        <div class="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            v-if="!v.is_active"
                            @click.stop="activateVersion(p.key, v.id)"
                            class="p-1.5 text-dark-tertiary hover:text-accent transition rounded" title="Activate"
                          ><Play class="w-3.5 h-3.5" /></button>
                          <button
                            v-if="authStore.isAdmin"
                            @click.stop="deleteVersion(p.key, v.id)"
                            class="p-1.5 text-dark-tertiary hover:text-error transition rounded"
                          ><Trash2 class="w-3.5 h-3.5" /></button>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <div class="border-b theme-border last:border-0">
            <button
              @click="toggleAiSection('models')"
              class="w-full flex items-center gap-3 pl-10 pr-6 py-3.5 hover:bg-dark-tertiary/50 transition text-left"
            >
              <div class="settings-accordion-icon flex-shrink-0">
                <Cpu class="w-4 h-4" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-dark-primary">Models & Pipeline</p>
                <p class="text-xs text-dark-secondary mt-0.5 truncate">
                  LLM, VLM, OCR and Semantic Settings
                </p>
              </div>
              <ChevronDown class="w-4 h-4 text-dark-tertiary transition-transform duration-200 flex-shrink-0" :class="{ 'rotate-180': aiOpenSection === 'models' }" />
            </button>

            <div v-if="aiOpenSection === 'models'" class="px-6 pb-5 pt-1">
              <div v-if="configLoading" class="py-6 text-center flex flex-col items-center gap-2 text-sm text-dark-secondary">
                <Loader2 class="w-5 h-5 animate-spin text-accent" /> Loading configuration...
              </div>
              <template v-else>
                <div class="bg-dark-tertiary/30 rounded-xl border theme-border p-5">
                  <div class="grid grid-cols-2 gap-8 mb-6">
                    <div class="space-y-4">
                      <p class="text-xs font-bold text-dark-secondary uppercase tracking-widest border-b theme-border pb-2">Inference Models</p>
                      <div>
                        <label class="settings-label">Chat Model <span class="text-dark-tertiary normal-case font-normal">(RAG chat)</span></label>
                        <input v-model="runtimeConfig.CHAT_MODEL" type="text" placeholder="e.g. gpt-5.4-mini" class="settings-input" />
                      </div>
                      <div>
                        <label class="settings-label">LLM Model <span class="text-dark-tertiary normal-case font-normal">(Verdict, Analysis)</span></label>
                        <input v-model="runtimeConfig.LLM_MODEL" type="text" placeholder="e.g. openai/gpt-oss-120b" class="settings-input" />
                      </div>
                      <div>
                        <label class="settings-label">VLM Model <span class="text-dark-tertiary normal-case font-normal">(Frame analysis)</span></label>
                        <input v-model="runtimeConfig.VLM_MODEL" type="text" placeholder="e.g. meta-llama/llama-4-scout" class="settings-input" />
                      </div>
                    </div>
                    <div class="space-y-4">
                      <p class="text-xs font-bold text-dark-secondary uppercase tracking-widest border-b theme-border pb-2">Processing Pipeline</p>
                      <div class="grid grid-cols-2 gap-4">
                        <div>
                          <label class="settings-label">Guacalyze FPS</label>
                          <input v-model.number="runtimeConfig.GUACALYZE_FPS" type="number" min="0.5" max="10" step="0.5" class="settings-input" />
                        </div>
                        <div>
                          <label class="settings-label">Event Threshold</label>
                          <input v-model.number="runtimeConfig.GUACALYZE_EVENT_THRESHOLD" type="number" min="1" max="20" class="settings-input" />
                        </div>
                      </div>
                      <div>
                        <label class="settings-label">OCR Concurrent Requests</label>
                        <input v-model.number="runtimeConfig.OCR_CONCURRENT_REQUESTS" type="number" min="1" max="200" class="settings-input" />
                      </div>
                      <div class="pt-2">
                        <label class="settings-label">
                          Semantic Search Threshold
                          <span class="text-accent font-bold ml-2">{{ runtimeConfig.SEARCH_SEMANTIC_THRESHOLD }}</span>
                        </label>
                        <input v-model.number="runtimeConfig.SEARCH_SEMANTIC_THRESHOLD" type="range" min="0.1" max="0.9" step="0.05" class="w-full" style="accent-color: var(--accent);" />
                        <div class="flex justify-between text-xs text-dark-tertiary mt-1">
                          <span>0.1 (Broader)</span><span>0.9 (Stricter)</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="flex items-center gap-3">
                    <button @click="saveConfig" :disabled="savingConfig" class="w-28 flex items-center justify-center py-2.5 bg-accent text-white rounded-lg text-sm font-semibold hover:opacity-90 transition disabled:opacity-50">
                      <Loader2 v-if="savingConfig" class="w-4 h-4 animate-spin mr-1.5" />
                      <span v-else>Save</span>
                    </button>
                    <button @click="aiOpenSection = null" class="w-28 flex items-center justify-center py-2.5 bg-dark-tertiary text-dark-primary border theme-border rounded-lg text-sm font-medium hover:bg-dark-quaternary transition">
                      Close
                    </button>
                    <span v-if="saveConfigSuccess" class="text-xs text-success flex items-center gap-1 ml-2"><Check class="w-3.5 h-3.5" /> Saved</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </section>

      <section v-if="authStore.isAdmin && authStore.hasTenant" class="settings-section !p-0 overflow-hidden">

        <div class="flex items-center gap-3 px-6 py-4 border-b theme-border">
          <div class="settings-section-icon flex-shrink-0"><Bell class="w-4 h-4" /></div>
          <div class="flex-1 min-w-0">
            <h2 class="settings-section-title">Alert notifications</h2>
            <p class="settings-section-desc">Configure email alerts for suspicious sessions.</p>
          </div>
          <div class="flex items-center gap-3 flex-shrink-0">
            <button
              @click="toggleNotificationEnabled"
              class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0"
              :style="notifStore.settings.enabled ? 'background: var(--accent)' : 'background: var(--bg-quaternary)'"
            >
              <span
                class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                :style="notifStore.settings.enabled ? 'transform: translateX(1.375rem)' : 'transform: translateX(0.25rem)'"
              />
            </button>
          </div>
        </div>

        <div v-if="notifStore.settingsLoading || notifStore.smtpLoading" class="flex items-center gap-2 px-6 py-5 text-sm text-dark-secondary">
          <Loader2 class="w-4 h-4 animate-spin" /> Loading…
        </div>

        <template v-else>
          <template v-if="notifStore.settings.enabled">

            <div class="border-b theme-border">
              <button
                @click="notifOpenSection = notifOpenSection === 'recipients' ? null : 'recipients'"
                class="w-full flex items-center gap-3 pl-10 pr-6 py-3.5 hover:bg-dark-tertiary/50 transition text-left"
              >
                <div class="settings-accordion-icon">
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-dark-primary">Recipients</p>
                  <p class="text-xs text-dark-secondary truncate mt-0.5">
                    <span v-if="notifStore.settings.recipients?.length">{{ notifStore.settings.recipients.join(', ') }}</span>
                    <span v-else class="italic text-dark-tertiary">No recipients added yet.</span>
                  </p>
                </div>
                <ChevronDown class="w-4 h-4 text-dark-tertiary transition-transform duration-200 flex-shrink-0" :class="{ 'rotate-180': notifOpenSection === 'recipients' }" />
              </button>
              <div v-if="notifOpenSection === 'recipients'" class="px-6 pb-5 pt-1 space-y-2">
                <div class="bg-dark-tertiary rounded-xl border theme-border p-3 space-y-1">
                  <div
                    v-for="(email, index) in notifStore.settings.recipients"
                    :key="email"
                    class="flex items-center justify-between py-1.5 border-b theme-border last:border-0"
                  >
                    <span class="text-sm text-dark-primary">{{ email }}</span>
                    <button @click="notifStore.removeRecipient(index)" class="p-1 text-dark-secondary hover:text-error transition rounded">
                      <X class="w-4 h-4" />
                    </button>
                  </div>
                  <div class="flex gap-2 pt-1">
                    <input
                      v-model="newRecipient"
                      type="email"
                      placeholder="Add email address…"
                      class="settings-input flex-1"
                      @keydown.enter="handleAddRecipient"
                    />
                    <button
                      @click="handleAddRecipient"
                      class="px-4 py-2 bg-dark-quaternary text-dark-primary rounded-lg text-sm font-medium hover:opacity-80 transition border theme-border"
                    >Add</button>
                  </div>
                </div>
              </div>
            </div>

            <div class="border-b theme-border">
              <button
                @click="notifOpenSection = notifOpenSection === 'delivery' ? null : 'delivery'"
                class="w-full flex items-center gap-3 pl-10 pr-6 py-3.5 hover:bg-dark-tertiary/50 transition text-left"
              >
                <div class="settings-accordion-icon">
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-dark-primary">Delivery modes</p>
                  <p class="text-xs text-dark-secondary mt-0.5">
                    Immediate ({{ (notifStore.settings.threshold ?? 'high').charAt(0).toUpperCase() + (notifStore.settings.threshold ?? 'high').slice(1) }}+) ·
                    Daily digest {{ notifStore.settings.daily_digest?.enabled ? 'on' : 'off' }} ·
                    Monthly {{ notifStore.settings.monthly?.enabled ? 'on' : 'off' }}
                  </p>
                </div>
                <ChevronDown class="w-4 h-4 text-dark-tertiary transition-transform duration-200 flex-shrink-0" :class="{ 'rotate-180': notifOpenSection === 'delivery' }" />
              </button>
              <div v-if="notifOpenSection === 'delivery'" class="px-6 pb-5 pt-1">
                <div class="bg-dark-tertiary rounded-xl border theme-border overflow-hidden">

                  <div class="flex items-center gap-4 px-4 py-3 border-b theme-border">
                    <button
                      class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0"
                      style="background: var(--accent);"
                      title="Always on"
                    >
                      <span class="inline-block h-3.5 w-3.5 transform rounded-full bg-white" style="transform: translateX(1.125rem)" />
                    </button>
                    <span class="text-xs font-semibold text-dark-primary bg-dark-quaternary px-2 py-0.5 rounded flex-shrink-0">Immediate</span>
                    <span class="text-xs text-dark-secondary flex-1">Per session, right after analysis ends.</span>
                    <div class="flex items-center gap-2 flex-shrink-0">
                      <span class="text-xs text-dark-secondary">Min. severity</span>
                      <select v-model="notifStore.settings.threshold" class="settings-select" style="height: 2rem; font-size: 0.75rem; padding: 0 0.5rem;">
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  <div class="flex items-center gap-4 px-4 py-3 border-b theme-border" :class="!notifStore.settings.daily_digest?.enabled ? 'opacity-60' : ''">
                    <button
                      @click="notifStore.settings.daily_digest = { ...notifStore.settings.daily_digest, enabled: !notifStore.settings.daily_digest?.enabled }"
                      class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0"
                      :style="notifStore.settings.daily_digest?.enabled ? 'background: var(--accent)' : 'background: var(--bg-quaternary)'"
                    >
                      <span class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform"
                        :style="notifStore.settings.daily_digest?.enabled ? 'transform: translateX(1.125rem)' : 'transform: translateX(0.2rem)'" />
                    </button>
                    <span class="text-xs font-semibold text-dark-primary bg-dark-quaternary px-2 py-0.5 rounded flex-shrink-0">Daily digest</span>
                    <span class="text-xs text-dark-secondary flex-1">One summary email per day with all flagged sessions.</span>
                    <div class="flex items-center gap-2 flex-shrink-0" :class="!notifStore.settings.daily_digest?.enabled ? 'pointer-events-none' : ''">
                      <span class="text-xs text-dark-secondary">Send at</span>
                      <input
                        v-model="notifStore.settings.daily_digest.send_at"
                        type="time"
                        class="settings-input"
                        style="height: 2rem; font-size: 0.75rem; padding: 0 0.5rem; width: 7rem;"
                      />
                    </div>
                  </div>

                  <div class="flex items-center gap-4 px-4 py-3" :class="!notifStore.settings.monthly?.enabled ? 'opacity-60' : ''">
                    <button
                      @click="notifStore.settings.monthly = { ...notifStore.settings.monthly, enabled: !notifStore.settings.monthly?.enabled }"
                      class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0"
                      :style="notifStore.settings.monthly?.enabled ? 'background: var(--accent)' : 'background: var(--bg-quaternary)'"
                    >
                      <span class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform"
                        :style="notifStore.settings.monthly?.enabled ? 'transform: translateX(1.125rem)' : 'transform: translateX(0.2rem)'" />
                    </button>
                    <span class="text-xs font-semibold text-dark-primary bg-dark-quaternary px-2 py-0.5 rounded flex-shrink-0">Monthly report</span>
                    <span class="text-xs text-dark-secondary flex-1">Trends, comparisons and executive summary.</span>
                    <div class="flex items-center gap-2 flex-shrink-0" :class="!notifStore.settings.monthly?.enabled ? 'pointer-events-none' : ''">
                      <span class="text-xs text-dark-secondary">Send on</span>
                      <select v-model.number="notifStore.settings.monthly.send_on_day" class="settings-select" style="height: 2rem; font-size: 0.75rem; padding: 0 0.5rem;">
                        <option v-for="d in 28" :key="d" :value="d">
                          {{ d === 1 ? '1st' : d === 2 ? '2nd' : d === 3 ? '3rd' : `${d}th` }}
                        </option>
                      </select>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <div class="border-b theme-border">
              <button
                @click="notifOpenSection = notifOpenSection === 'triggers' ? null : 'triggers'"
                class="w-full flex items-center gap-3 pl-10 pr-6 py-3.5 hover:bg-dark-tertiary/50 transition text-left"
              >
                <div class="settings-accordion-icon">
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-dark-primary">Triggers</p>
                  <p class="text-xs text-dark-secondary mt-0.5 truncate">
                    <span v-if="notifStore.settings.tactics?.length">
                      {{ notifStore.availableTactics.filter(t => notifStore.isTacticActive(t.id)).map(t => t.name).join(', ') }}
                    </span>
                    <span v-else class="italic text-dark-tertiary">No triggers selected.</span>
                  </p>
                </div>
                <ChevronDown class="w-4 h-4 text-dark-tertiary transition-transform duration-200 flex-shrink-0" :class="{ 'rotate-180': notifOpenSection === 'triggers' }" />
              </button>
              <div v-if="notifOpenSection === 'triggers'" class="px-6 pb-5 pt-1 space-y-1.5">
                <button
                  v-for="tactic in notifStore.availableTactics"
                  :key="tactic.id"
                  @click="notifStore.toggleTactic(tactic.id)"
                  class="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl border text-left transition-colors"
                  :class="notifStore.isTacticActive(tactic.id)
                    ? 'border-accent/40 bg-accent/5'
                    : 'theme-border bg-dark-tertiary hover:bg-dark-quaternary'"
                >
                  <div
                    class="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors"
                    :class="notifStore.isTacticActive(tactic.id) ? 'border-accent bg-accent' : 'theme-border bg-dark-primary'"
                  >
                    <Check v-if="notifStore.isTacticActive(tactic.id)" class="w-2.5 h-2.5 text-white" />
                  </div>
                  <span
                    class="text-xs font-mono flex-shrink-0 w-14"
                    :class="notifStore.isTacticActive(tactic.id) ? 'text-accent' : 'text-dark-tertiary'"
                  >{{ tactic.id }}</span>
                  <span
                    class="text-sm flex-1"
                    :class="notifStore.isTacticActive(tactic.id) ? 'text-dark-primary font-medium' : 'text-dark-secondary'"
                  >{{ tactic.name }}</span>
                  <span
                    class="text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase flex-shrink-0"
                    :class="{
                      'bg-error/10 border-error/30 text-error':   tactic.severity === 'critical',
                      'bg-warning/10 border-warning/30 text-warning': tactic.severity === 'high',
                      'bg-accent/10 border-accent/30 text-accent': tactic.severity === 'medium',
                    }"
                  >{{ tactic.severity }}</span>
                </button>
                <p class="text-xs text-dark-tertiary pt-1">
                  Select which tactics should trigger an immediate alert. An alert is sent when <span class="font-semibold text-dark-secondary">any selected tactic</span> is identified in a session.
                </p>
              </div>
            </div>

            <div>
              <button
                @click="notifOpenSection = notifOpenSection === 'smtp' ? null : 'smtp'"
                class="w-full flex items-center gap-3 pl-10 pr-6 py-3.5 hover:bg-dark-tertiary/50 transition text-left"
              >
                <div class="settings-accordion-icon">
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-dark-primary">SMTP</p>
                  <p class="text-xs text-dark-secondary mt-0.5 font-mono truncate">
                    <span v-if="notifStore.smtp.host">{{ notifStore.smtp.host }} · port {{ notifStore.smtp.port }}{{ notifStore.smtp.tls ? ' · TLS' : '' }}</span>
                    <span v-else class="italic not-italic" style="font-family: var(--font-sans);">Not configured.</span>
                  </p>
                </div>
                <ChevronDown class="w-4 h-4 text-dark-tertiary transition-transform duration-200 flex-shrink-0" :class="{ 'rotate-180': notifOpenSection === 'smtp' }" />
              </button>
              <div v-if="notifOpenSection === 'smtp'" class="px-6 pb-5 pt-1">
                <div class="grid grid-cols-2 gap-4">
                  <div><label class="settings-label">Host</label><input v-model="notifStore.smtp.host" type="text" placeholder="smtp.sendgrid.net" class="settings-input" /></div>
                  <div><label class="settings-label">Port</label><input v-model.number="notifStore.smtp.port" type="number" placeholder="587" class="settings-input" /></div>
                  <div><label class="settings-label">User</label><input v-model="notifStore.smtp.user" type="text" placeholder="apikey" class="settings-input" /></div>
                  <div><label class="settings-label">Password</label><input v-model="notifStore.smtp.password" type="password" placeholder="••••••••" class="settings-input" /></div>
                  <div><label class="settings-label">From address</label><input v-model="notifStore.smtp.from" type="email" placeholder="noreply@empresa.com" class="settings-input" /></div>
                  <div class="flex items-end pb-1">
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" v-model="notifStore.smtp.tls" style="accent-color: var(--accent);" />
                      <span class="text-sm text-dark-primary">Use TLS</span>
                    </label>
                  </div>
                </div>
                <p v-if="notifStore.smtpError" class="text-xs text-error mt-3">{{ notifStore.smtpError }}</p>
                <div class="flex items-center gap-3 mt-4">
                  <span v-if="notifStore.testResult?.sent === true" class="text-xs text-success flex items-center gap-1">
                    <Check class="w-3.5 h-3.5" /> Test email sent.
                  </span>
                  <span v-else-if="notifStore.testResult?.sent === false" class="text-xs text-error">
                    {{ notifStore.testResult.error ?? 'Failed to send test email.' }}
                  </span>
                  <button
                    @click="notifStore.runTestEmail"
                    :disabled="notifStore.testSending || !notifStore.settings.recipients?.length"
                    class="px-4 py-2 bg-dark-tertiary text-dark-primary rounded-lg text-sm font-medium hover:bg-dark-quaternary transition border theme-border disabled:opacity-40"
                  >
                    <Loader2 v-if="notifStore.testSending" class="w-4 h-4 animate-spin inline mr-1" />
                    {{ notifStore.testSending ? 'Testing…' : 'Test' }}
                  </button>
                </div>
              </div>
            </div>

            <div class="flex items-center justify-end gap-3 px-6 py-4 border-t theme-border bg-dark-tertiary/30">
              <span v-if="notifStore.settingsError" class="text-xs text-error flex-1">{{ notifStore.settingsError }}</span>
              <button
                @click="async () => { await notifStore.saveSmtp(); await notifStore.saveSettings(); captureNotifSnapshot(); }"
                :disabled="!notifDirty || notifStore.settingsSaving || notifStore.smtpSaving"
                class="w-28 flex items-center justify-center py-2.5 bg-accent text-white rounded-lg text-sm font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Loader2 v-if="notifStore.settingsSaving || notifStore.smtpSaving" class="w-4 h-4 animate-spin mr-1.5" />
                <span v-else>Save</span>
              </button>
              <span v-if="notifStore.settingsSaved && notifStore.smtpSaved && !notifDirty" class="text-xs text-success flex items-center gap-1">
                <Check class="w-3.5 h-3.5" /> Saved.
              </span>
            </div>

          </template></template></section>

      <Teleport v-if="showDeleteAccount" to="#modal-root">
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" @click="showDeleteAccount = false; deleteConfirmText = ''">
          <div class="bg-dark-primary border theme-border rounded-2xl shadow-xl p-6 w-full max-w-sm space-y-4" @click.stop>
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style="background: rgba(255,144,95,0.12);">
                <Trash2 class="w-5 h-5" style="color: #ff905f;" />
              </div>
              <div>
                <h3 class="text-lg font-bold text-dark-primary">Delete account?</h3>
                <p class="text-xs text-dark-secondary mt-0.5">This action cannot be undone.</p>
              </div>
            </div>
            <p class="text-sm text-dark-secondary leading-relaxed">
              Your account, workspace <span class="font-mono text-dark-primary">{{ authStore.tenantId }}</span>, all members, invites and configuration will be permanently deleted.
            </p>
            <div>
              <label class="settings-label mb-1.5 block">Type <span class="font-mono font-bold" style="color: #ff905f;">DELETE</span> to confirm</label>
              <input v-model="deleteConfirmText" type="text" placeholder="DELETE" class="settings-input" autocomplete="off" spellcheck="false" />
            </div>
            <div class="flex gap-3 pt-2">
              <button @click="showDeleteAccount = false; deleteConfirmText = ''" class="flex-1 px-4 py-2 rounded-lg text-sm transition" style="background: var(--bg-tertiary); color: var(--text-primary);">Cancel</button>
              <button @click="deleteAccount" :disabled="deletingAccount || deleteConfirmText !== 'DELETE'" class="flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition" style="background: #ff905f; color: #fff;"
                :style="(deletingAccount || deleteConfirmText !== 'DELETE') ? 'opacity: 0.4; cursor: not-allowed; background: #ff905f' : 'background: #ff905f'">
                <Loader2 v-if="deletingAccount" class="w-4 h-4 animate-spin inline mr-1" />
                {{ deletingAccount ? 'Deleting…' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </Teleport>

      <Teleport v-if="confirm.show" to="#modal-root">
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" @click="confirm.show = false">
          <div class="bg-dark-primary border theme-border rounded-2xl shadow-xl p-6 w-full max-w-sm space-y-4" @click.stop>
            <h3 class="text-lg font-bold text-dark-primary">{{ confirm.title }}</h3>
            <p class="text-sm text-dark-secondary">{{ confirm.message }}</p>
            <div class="flex gap-3 pt-2">
              <button @click="confirm.show = false" class="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition" style="background: var(--bg-tertiary); color: var(--text-primary);">Cancel</button>
              <button @click="confirm.action" class="flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition" style="background: #ff905f; color: #fff;">{{ confirm.label }}</button>
            </div>
          </div>
        </div>
      </Teleport>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick, toRaw } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../stores/authStore";
import { useSessionStore } from "../stores/sessionStore";
import {
  logoutUser, saveBackendConfig, createTenantInFirestore, updateTenantSlug,
  deleteTenant, inviteUser, loadTenantUsers, loadTenantInvites,
  removeUser, updateUserRole, revokeInvite, _acceptInvitePublic,
  resolveTenant, leaveWorkspace, disconnectBackend, loadTenantConfig,
  createAdditionalWorkspace,
} from "../services/authService";
import { apiClient } from "../services/apiClient";
import { useNotificationStore } from "../stores/notificationStore";
import { collection, query, where, onSnapshot, deleteDoc, doc, setDoc, serverTimestamp, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { UserCircle, Server, Sparkles, Eye, EyeOff, Loader2, Check, Trash2, Mail, X, LogOut, Pencil, Unlink, Activity, Link, Bell, Terminal, MessageSquare, Wrench, Cpu, Search, Brain, ChevronDown, Users, Play } from "lucide-vue-next";

const router    = useRouter();
const authStore    = useAuthStore();
const sessionStore = useSessionStore();
const notifStore = useNotificationStore();

// ── Notification recipients input ─────────────────────────────────────────
const newRecipient = ref("");
const notifOpenSection = ref(null); 
const notifSnapshot = ref(null);    
const accountOpenSection = ref(null);

const notifDirty = computed(() => {
  if (!notifSnapshot.value) return false;
  void notifStore.settings.enabled;
  void notifStore.settings.recipients;
  void notifStore.settings.threshold;
  void notifStore.settings.tactics;
  void notifStore.settings.daily_digest;
  void notifStore.settings.monthly;
  void notifStore.smtp.host;
  void notifStore.smtp.port;
  void notifStore.smtp.user;
  void notifStore.smtp.password;
  void notifStore.smtp.from;
  void notifStore.smtp.tls;
  const current = JSON.stringify({
    settings: toRaw(notifStore.settings),
    smtp:     toRaw(notifStore.smtp),
  });
  return current !== notifSnapshot.value;
});

function captureNotifSnapshot() {
  notifSnapshot.value = JSON.stringify({
    settings: toRaw(notifStore.settings),
    smtp:     toRaw(notifStore.smtp),
  });
}

function handleAddRecipient() {
  if (notifStore.addRecipient(newRecipient.value)) {
    newRecipient.value = "";
  }
}

async function toggleNotificationEnabled() {
  notifStore.settings.enabled = !notifStore.settings.enabled;
  if (!notifStore.settings.enabled) {
    await notifStore.saveSettings();
    captureNotifSnapshot();
  }
}

// ── Account ───────────────────────────────────────────────────────────────
const userInitial = computed(() => (authStore.user?.email ?? "?")[0].toUpperCase());
const roleClass   = computed(() => ({ admin: "role-badge-admin", editor: "role-badge-editor", viewer: "role-badge-viewer" }[authStore.role] ?? ""));

const showDeleteAccount = ref(false);
const deletingAccount   = ref(false);
const deleteConfirmText = ref("");

async function deleteAccount() {
  if (deleteConfirmText.value !== "DELETE") return;
  deletingAccount.value = true;
  try {
    if (authStore.tenantId) await deleteTenant(authStore.tenantId, authStore.user.uid);
    await logoutUser();
    authStore.clearUser();
    router.push("/login");
  } catch (err) {
    console.error("[Settings] Delete account error:", err);
    deletingAccount.value = false;
    deleteConfirmText.value = "";
  }
}

// ── Users ─────────────────────────────────────────────────────────────────
const users   = ref([]);
const invites = ref([]);
const inviteEmail = ref(""); const inviteRole = ref("viewer");
const inviting = ref(false); const inviteSuccess = ref(""); const inviteError = ref("");
const pendingReceivedInvites = ref([]);
let unsubscribeInvites = null;

function startInviteListener() {
  if (!authStore.user?.email) return;
  unsubscribeInvites = onSnapshot(
    query(collection(db, "invites"), where("email", "==", authStore.user.email), where("status", "==", "pending")),
    (snap) => { pendingReceivedInvites.value = snap.docs.map(d => ({ id: d.id, ...d.data() })); }
  );
}

async function handleAcceptInvite(inv) {
  await _acceptInvitePublic(inv, authStore.user.uid, authStore.user.email);
  await setDoc(doc(db, "tenants", inv.tenantId, "notifications", `accept_${authStore.user.uid}_${Date.now()}`), {
    type: "invite_accepted", uid: authStore.user.uid, email: authStore.user.email,
    tenantId: inv.tenantId, message: `${authStore.user.email} accepted the invite`, read: false, created_at: serverTimestamp(),
  });
  const result = await resolveTenant(authStore.user.uid, authStore.user.email);
  if (result) authStore.setTenant(result);
}

async function handleDeclineInvite(inv) {
  await deleteDoc(doc(db, "invites", inv.email));
  await deleteDoc(doc(db, "tenants", inv.tenantId, "invites", inv.email)).catch(() => {});
  await setDoc(doc(db, "tenants", inv.tenantId, "notifications", `decline_${authStore.user.uid}_${Date.now()}`), {
    type: "invite_declined", uid: authStore.user.uid, email: authStore.user.email,
    tenantId: inv.tenantId, message: `${authStore.user.email} declined the invite`, read: false, created_at: serverTimestamp(),
  });
}

async function sendInvite() {
  if (!inviteEmail.value || inviting.value) return;
  inviting.value = true; inviteSuccess.value = ""; inviteError.value = "";
  try {
    await inviteUser(authStore.tenantId, authStore.user.uid, { email: inviteEmail.value, role: inviteRole.value });
    inviteSuccess.value = inviteEmail.value;
    setTimeout(() => { inviteSuccess.value = ""; }, 3000);
    inviteEmail.value = "";
  } catch (err) {
    inviteError.value = "Failed to send invite. Try again.";
  } finally {
    inviting.value = false;
  }
}

async function changeRole(uid, role) {
  await updateUserRole(authStore.tenantId, uid, role);
}

const confirm = ref({ show: false, title: "", message: "", label: "", action: null });

function leaveWorkspaceConfirm(t) {
  confirm.value = { show: true, title: "Leave workspace?", message: `You will lose access to ${t.tenantId}.`, label: "Leave",
    action: async () => { await leaveWorkspace(t.tenantId, authStore.user.uid, authStore.user.email); confirm.value.show = false; }
  };
}

function removeUserConfirm(u) {
  confirm.value = { show: true, title: "Remove user?", message: `${u.email ?? u.id} will lose access to this workspace.`, label: "Remove",
    action: async () => { await removeUser(authStore.tenantId, u.id); confirm.value.show = false; }
  };
}

function revokeInviteConfirm(inv) {
  confirm.value = { show: true, title: "Revoke invite?", message: `The invite for ${inv.email} will be cancelled.`, label: "Revoke",
    action: async () => { await revokeInvite(authStore.tenantId, inv.email); confirm.value.show = false; }
  };
}

function roleBadgeClass(role) {
  return { admin: "role-badge-admin", editor: "role-badge-editor", viewer: "role-badge-viewer" }[role] ?? "";
}

// Admin notifications
const adminNotifications = ref([]);
let unsubNotifications = null;
function startNotificationsListener() {
  if (!authStore.isAdmin || !authStore.tenantId) return;
  unsubNotifications = onSnapshot(
    query(collection(db, "tenants", authStore.tenantId, "notifications"), where("read", "==", false)),
    (snap) => { adminNotifications.value = snap.docs.map(d => ({ id: d.id, ...d.data() })); }
  );
}

// ── Workspace ─────────────────────────────────────────────────────────────
const TENANT_REQUEST_URL = import.meta.env.DEV
  ? "/watchctl-proxy/tenantrequest"
  : "https://watchctl.seguraw.ai/tenantrequest";

const allWorkspaces = computed(() => authStore.tenants ?? []);

// When Firestore listener adds new tenants, initialize their config immediately
watch(allWorkspaces, (tenants) => {
  tenants.forEach(t => {
    if (!workspaceConfigs.value[t.tenantId]) {
      initWorkspaceConfig(t.tenantId, {});
    }
  });
}, { immediate: false });
const workspaceConfigs    = ref({});
const editingWorkspace    = ref({});
const enteringCredentials = ref({});
const savingWorkspace     = ref({});
const savedWorkspace      = ref({});
const provisioning        = ref({});
const provisionMsg        = ref({});
const provisionError      = ref({});
const testingConnection   = ref({});
const testResult          = ref({});
const wsListeners         = ref(null);
const creatingWorkspace   = ref(false);
const isAllowedOrigin = import.meta.env.DEV || window.location.origin === "https://watch.segura0.ai";

async function createWorkspace() {
  if (creatingWorkspace.value) return;
  if ((authStore.ownedTenantIds?.length ?? 0) >= 3) return;
  const activeCfg = workspaceConfigs.value[authStore.tenantId];
  if (!activeCfg?.apiBase || !activeCfg?.apiKey) return;
  creatingWorkspace.value = true;
  try {
    const slug = await createAdditionalWorkspace(authStore.user.uid, authStore.user.email);
    // tenants array is updated automatically by the onSnapshot listener in main.js
    // only update ownedTenantIds since the listener doesn't track ownership
    authStore.setTenant({ ownedTenantIds: [...(authStore.ownedTenantIds ?? []), slug] });
    initWorkspaceConfig(slug, {});
  } catch (err) {
    console.error("[Settings] Failed to create workspace:", err);
  } finally {
    creatingWorkspace.value = false;
  }
}

const switchingTenant = ref(null);
async function switchWorkspace(tenantId) {
  if (tenantId === authStore.tenantId || switchingTenant.value) return;
  switchingTenant.value = tenantId;
  try {
    const tenant = await resolveTenant(authStore.user.uid, authStore.user.email, tenantId);
    if (tenant) {
      authStore.setTenant(tenant);
      sessionStore.sessions = [];
      sessionStore.loadSessions();
    }
  } catch (err) {
    console.error("[Settings] Switch workspace error:", err);
  } finally {
    switchingTenant.value = null;
  }
}

async function checkConnectivity(apiBase, apiKey) {
  try {
    const res = await fetch(`${apiBase}/system/health`, {
      headers: { "X-API-Key": apiKey },
      signal: AbortSignal.timeout(5000),
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function testConnection(tenantId) {
  const cfg = workspaceConfigs.value[tenantId];
  const urlToTest = editingWorkspace.value[tenantId] ? cfg?.editBase : cfg?.apiBase;
  const keyToTest = editingWorkspace.value[tenantId] ? cfg?.editKey  : cfg?.apiKey;
  if (!urlToTest || !keyToTest) return;
  
  testingConnection.value[tenantId] = true;
  testResult.value[tenantId] = null;
  try {
    const ok = await checkConnectivity(urlToTest, keyToTest);
    testResult.value[tenantId] = ok ? 'ok' : 'error';
    if (cfg) cfg.isConnected = ok;
  } catch {
    testResult.value[tenantId] = 'error';
    if (cfg) cfg.isConnected = false;
  } finally {
    testingConnection.value[tenantId] = false;
    setTimeout(() => { testResult.value[tenantId] = null; }, 3000);
  }
}

function initWorkspaceConfig(tenantId, cfg = {}) {
  if (!workspaceConfigs.value[tenantId]) {
    workspaceConfigs.value[tenantId] = {
      apiBase:     cfg.apiBase ?? null,
      apiKey:      cfg.apiKey  ?? null,
      editBase:    cfg.apiBase ?? "",
      editKey:     cfg.apiKey  ?? "",
      showKey:     false,
      isConnected: !!cfg.apiBase,
      isSoleAdmin: false,
    };
  }
}

async function loadAllWorkspaceConfigs() {
  for (const t of authStore.tenants ?? []) {
    const cfg = await loadTenantConfig(t.tenantId);
    initWorkspaceConfig(t.tenantId, cfg);
    // Real connectivity check — don't assume connected just because apiBase is set
    if (cfg?.apiBase && cfg?.apiKey) {
      const ok = await checkConnectivity(cfg.apiBase, cfg.apiKey);
      workspaceConfigs.value[t.tenantId].isConnected = ok;
    } else {
      workspaceConfigs.value[t.tenantId].isConnected = false;
    }
    // Check if user is the sole admin — if so, Leave would orphan the workspace
    if (t.role === 'admin' && !authStore.ownedTenantIds?.includes(t.tenantId)) {
      const snap = await getDocs(collection(db, "tenants", t.tenantId, "users"));
      const adminCount = snap.docs.filter(d => d.data().role === 'admin').length;
      workspaceConfigs.value[t.tenantId].isSoleAdmin = adminCount <= 1;
    }
  }
}

function toggleEdit(tenantId) {
  if (editingWorkspace.value[tenantId]) {
    editingWorkspace.value[tenantId] = false;
  } else {
    const cfg = workspaceConfigs.value[tenantId];
    if (cfg) { cfg.editBase = cfg.apiBase ?? ""; cfg.editKey = cfg.apiKey ?? ""; }
    editingWorkspace.value[tenantId] = true;
  }
}

async function saveWorkspaceConfig(tenantId) {
  const cfg = workspaceConfigs.value[tenantId];
  if (!cfg) return;
  savingWorkspace.value[tenantId] = true;
  try {
    await saveBackendConfig(tenantId, authStore.user.uid, { apiBase: cfg.editBase, apiKey: cfg.editKey });
    cfg.apiBase = cfg.editBase;
    cfg.apiKey  = cfg.editKey;
    cfg.isConnected = await checkConnectivity(cfg.editBase, cfg.editKey);
    
    editingWorkspace.value[tenantId]    = false;
    enteringCredentials.value[tenantId] = false;
    savedWorkspace.value[tenantId]      = true;
    setTimeout(() => { savedWorkspace.value[tenantId] = false; }, 3000);
    if (tenantId === authStore.tenantId) {
      authStore.setTenant({ ...authStore.$state, apiBase: cfg.editBase, apiKey: cfg.editKey });
    }
  } catch (err) {
    console.error("[Settings] Save workspace config error:", err);
  } finally {
    savingWorkspace.value[tenantId] = false;
  }
}

function disconnectConfirm(tenantId) {
  confirm.value = {
    show: true,
    title: "Disconnect backend?",
    message: `This will disconnect the API for ${tenantId}. Your URL and API Key will be preserved so you can reconnect later.`,
    label: "Disconnect",
    action: async () => {
      const cfg = workspaceConfigs.value[tenantId];
      try {
        await saveBackendConfig(tenantId, authStore.user.uid, { apiBase: null, apiKey: cfg.apiKey });
        if (cfg) {
          cfg.isConnected = false;
        }
        if (tenantId === authStore.tenantId) {
          authStore.setTenant({ ...authStore.$state, apiBase: null });
        }
      } catch(e) {
        console.error(e);
      }
      confirm.value.show = false;
    },
  };
}

async function reconnectWorkspace(tenantId) {
  const cfg = workspaceConfigs.value[tenantId];
  if (cfg && cfg.apiBase && cfg.apiKey) {
    const ok = await checkConnectivity(cfg.apiBase, cfg.apiKey);
    cfg.isConnected = ok;
    if (ok) {
      try {
        await saveBackendConfig(tenantId, authStore.user.uid, { apiBase: cfg.apiBase, apiKey: cfg.apiKey });
        if (tenantId === authStore.tenantId) {
          authStore.setTenant({ ...authStore.$state, apiBase: cfg.apiBase, apiKey: cfg.apiKey });
        }
      } catch(e) { console.error(e); }
    }
  } else {
    editingWorkspace.value[tenantId] = true;
  }
}

function deleteWorkspaceConfirm(tenantId) {
  confirm.value = {
    show: true,
    title: "Delete workspace?",
    message: `This will permanently delete ${tenantId}, all its members, invites and configuration. This cannot be undone.`,
    label: "Delete",
    action: async () => {
      await deleteTenant(tenantId, authStore.user.uid);
      confirm.value.show = false;
      if (tenantId === authStore.tenantId) {
        await logoutUser();
        authStore.clearUser();
        router.push("/login");
      } else {
        // Optimistically remove from local state
        authStore.setTenant({
          ...authStore.$state,
          tenants: (authStore.tenants ?? []).filter(t => t.tenantId !== tenantId),
          ownedTenantIds: (authStore.ownedTenantIds ?? []).filter(id => id !== tenantId),
        });
        delete workspaceConfigs.value[tenantId];
      }
    },
  };
}

async function requestProvisioning(tenantId) {
  provisioning.value[tenantId]   = true;
  provisionMsg.value[tenantId]   = "";
  provisionError.value[tenantId] = "";
  try {
    const res = await fetch(TENANT_REQUEST_URL, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ email: authStore.user.email }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    provisionMsg.value[tenantId] = "Request sent! You'll receive credentials by email.";
  } catch (err) {
    provisionError.value[tenantId] = "Request failed. Try again or contact support.";
  } finally {
    provisioning.value[tenantId] = false;
  }
}

// ── AI Configuration ───────────────────────────────────────────────────────
const PROMPT_SECTIONS = [
  { key: "system",       label: "System Prompt",  icon: Terminal },
  { key: "verdict",      label: "Verdict Prompt", icon: Brain },
  { key: "chat_query",   label: "Chat Prompt",    icon: MessageSquare },
  { key: "vision_frame", label: "Vision Prompt",  icon: Eye },
  { key: "trusted_tools",label: "Tools Prompt",   icon: Wrench },
];

const aiOpenSection = ref(null); 
const promptsLoading = ref({});
const savingPrompt = ref({});
const savePromptSuccess = ref({});
const promptState = ref({});

// Controle de edição inline de nome da versão
const editingNoteId = ref(null);
const editNoteText = ref("");

function initPromptState() {
  PROMPT_SECTIONS.forEach(p => {
    promptState.value[p.key] = { editContent: "", originalContent: "", defaultContent: "", note: "", source: "file", activeVersion: null, versions: [] };
  });
}

async function loadPromptTab(key) {
  promptsLoading.value[key] = true;
  try {
    const [current, versionsRes, defaultRes] = await Promise.all([
      apiClient(`/prompts/${key}`),
      apiClient(`/prompts/${key}/versions`),
      apiClient(`/prompts/defaults/${key}`),
    ]);
    if (!promptState.value[key]) return;
    const state = promptState.value[key];
    state.editContent    = current?.content ?? "";
    state.originalContent = current?.content ?? "";
    state.source         = current?.source  ?? "file";
    state.activeVersion  = current?.version ?? null;
    state.versions       = versionsRes?.versions ?? [];
    state.defaultContent = defaultRes?.content   ?? "";
    state.note           = "";
  } catch (err) {
    console.error("[Settings] Failed to load prompt:", key, err);
  } finally {
    promptsLoading.value[key] = false;
  }
}

// Silent reload — syncs state without showing loading spinner (no flicker)
async function syncPromptTab(key) {
  try {
    const [current, versionsRes] = await Promise.all([
      apiClient(`/prompts/${key}`),
      apiClient(`/prompts/${key}/versions`),
    ]);
    if (!promptState.value[key]) return;
    const state = promptState.value[key];
    state.source        = current?.source  ?? "file";
    state.activeVersion = current?.version ?? null;
    state.versions      = versionsRes?.versions ?? [];
  } catch (err) {
    console.error("[Settings] Failed to sync prompt:", key, err);
  }
}

async function toggleAiSection(key) {
  if (aiOpenSection.value === key) {
    aiOpenSection.value = null; 
    return;
  }
  aiOpenSection.value = key;
  if (key === 'models' || key === 'search') {
    await loadConfig();
  } else {
    await loadPromptTab(key);
  }
}

async function savePromptVersion(key) {
  savingPrompt.value[key] = true;
  savePromptSuccess.value[key] = false;
  try {
    const res = await apiClient(`/prompts/${key}/versions`, {
      method: "POST",
      body: JSON.stringify({ content: promptState.value[key].editContent.trim(), note: promptState.value[key].note }),
    });
    // Optimistic update — mark all others inactive, add/set new version as active
    const state = promptState.value[key];
    if (state && res) {
      state.versions.forEach(v => { v.is_active = false; });
      const existing = state.versions.find(v => v.id === res.id);
      if (existing) {
        Object.assign(existing, res, { is_active: true });
      } else {
        state.versions.unshift({ ...res, is_active: true });
      }
      state.source = "db";
      state.activeVersion = res.id;
      state.note = "";
      state.originalContent = state.editContent; // new baseline
    }
    savePromptSuccess.value[key] = true;
    setTimeout(() => savePromptSuccess.value[key] = false, 3000);
  } catch (err) {
    console.error("[Settings] Save prompt error:", err);
  } finally {
    savingPrompt.value[key] = false;
  }
}

async function previewVersion(key, versionId) {
  if (editingNoteId.value) return;
  const state = promptState.value[key];
  if (!state) return;
  if (versionId === null) {
    state.editContent = state.defaultContent;
    state.originalContent = state.defaultContent;
  } else {
    const version = await apiClient(`/prompts/${key}/versions/${versionId}`);
    if (version) {
      state.editContent = version.content ?? "";
      state.originalContent = version.content ?? "";
    }
  }
}

function startEditNote(v) {
  editingNoteId.value = v.id;
  editNoteText.value = v.note || "";
}

async function saveNote(key, versionId) {
  // Guard: only save if this specific version was being edited
  if (editingNoteId.value !== versionId) return;
  const newNote = editNoteText.value.trim();
  editingNoteId.value = null;
  editNoteText.value = "";
  if (!newNote) return;

  const state = promptState.value[key];
  const version = state?.versions.find(v => v.id === versionId);
  if (!version || version.note === newNote) return;

  const prev = version.note;
  version.note = newNote;

  try {
    await apiClient(`/prompts/${key}/versions/${versionId}`, {
      method: "PATCH",
      body: JSON.stringify({ note: newNote })
    });
  } catch (err) {
    console.error("[Settings] Edit note error:", err);
    version.note = prev;
  }
}

async function activateVersion(key, versionId) {
  try {
    await apiClient(`/prompts/${key}/versions/${versionId}/activate`, { method: "PUT" });
    // Optimistic: update active flags
    const state = promptState.value[key];
    if (state) {
      state.versions.forEach(v => { v.is_active = v.id === versionId; });
      state.source = "db";
      state.activeVersion = versionId;
    }
    // Fetch content of the specific version (no race condition)
    const version = await apiClient(`/prompts/${key}/versions/${versionId}`);
    if (version && state) {
      state.editContent = version.content ?? "";
      state.originalContent = version.content ?? "";
    }
  } catch (err) { console.error(err); }
}

async function deleteVersion(key, versionId) {
  const state = promptState.value[key];
  const wasActive = state?.versions.find(v => v.id === versionId)?.is_active;
  // Optimistic: remove immediately
  if (state) state.versions = state.versions.filter(v => v.id !== versionId);
  try {
    await apiClient(`/prompts/${key}/versions/${versionId}`, { method: "DELETE" });
    if (wasActive) {
      // Sync quietly to learn new source/active state
      if (state.versions.length === 0) state.source = "file";
      await syncPromptTab(key);
    }
  } catch (err) {
    console.error(err);
    await syncPromptTab(key); // revert silently on error
  }
}

async function resetPromptToDefault(key) {
  const state = promptState.value[key];
  if (!state) return;
  const sorted = [...state.versions].sort((a, b) => (a.is_active ? 1 : -1));
  for (const v of sorted) {
    try {
      await apiClient(`/prompts/${key}/versions/${v.id}`, { method: "DELETE" });
    } catch { /* active may fail — continue */ }
  }
  await syncPromptTab(key);
}

// ── Runtime Config ─────────────────────────────────────────────────────────
const runtimeConfig = ref({
  SEARCH_SEMANTIC_THRESHOLD: 0.3, CHAT_MODEL: "", LLM_MODEL: "", VLM_MODEL: "",
  GUACALYZE_FPS: 2.0, GUACALYZE_EVENT_THRESHOLD: 3, OCR_CONCURRENT_REQUESTS: 50,
});
const configLoading = ref(false);
const savingConfig = ref(false);
const saveConfigSuccess = ref(false);

async function loadConfig() {
  configLoading.value = true;
  try {
    const items = await apiClient("/config");
    if (!items) return;
    items.forEach(item => {
      const key = item.key;
      if (key in runtimeConfig.value) {
        const v = item.value;
        runtimeConfig.value[key] = ["SEARCH_SEMANTIC_THRESHOLD", "GUACALYZE_FPS"].includes(key)
          ? parseFloat(v)
          : ["GUACALYZE_EVENT_THRESHOLD", "OCR_CONCURRENT_REQUESTS"].includes(key)
          ? parseInt(v) : v;
      }
    });
  } catch (err) { console.error(err); } finally { configLoading.value = false; }
}

async function saveConfig() {
  savingConfig.value = true; saveConfigSuccess.value = false;
  try {
    const values = {};
    Object.entries(runtimeConfig.value).forEach(([k, v]) => { values[k] = String(v); });
    await apiClient("/config", { method: "PUT", body: JSON.stringify({ values }) });
    saveConfigSuccess.value = true;
    setTimeout(() => saveConfigSuccess.value = false, 3000);
  } catch (err) { console.error(err); } finally { savingConfig.value = false; }
}

function formatDate(ts) {
  if (!ts) return "";
  return new Date(ts * 1000).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

onMounted(async () => {
  startInviteListener();
  startNotificationsListener();

  if (authStore.isAdmin && authStore.tenantId) {
    onSnapshot(collection(db, "tenants", authStore.tenantId, "users"), (snap) => {
      users.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    });
    onSnapshot(
      query(collection(db, "tenants", authStore.tenantId, "invites"), where("status", "==", "pending")),
      (snap) => { invites.value = snap.docs.map(d => ({ id: d.id, ...d.data() })); }
    );
  }

  await loadAllWorkspaceConfigs();

  // Keep the active workspace badge in sync with the real WebSocket state
  const onWsConnected    = () => { if (authStore.tenantId && workspaceConfigs.value[authStore.tenantId]) workspaceConfigs.value[authStore.tenantId].isConnected = true; };
  const onWsDisconnected = () => { if (authStore.tenantId && workspaceConfigs.value[authStore.tenantId]) workspaceConfigs.value[authStore.tenantId].isConnected = false; };
  window.addEventListener("ws:backend-connected",    onWsConnected);
  window.addEventListener("ws:backend-disconnected", onWsDisconnected);
  wsListeners.value = { onWsConnected, onWsDisconnected };

  if (authStore.hasTenant) {
    await notifStore.init();
    await nextTick();
    captureNotifSnapshot();
  }

  if ((authStore.isAdmin || authStore.isEditor) && authStore.hasTenant) {
    initPromptState();
  }
});

onUnmounted(() => {
  if (unsubscribeInvites) unsubscribeInvites();
  if (unsubNotifications) unsubNotifications();
  if (wsListeners.value) {
    window.removeEventListener("ws:backend-connected",    wsListeners.value.onWsConnected);
    window.removeEventListener("ws:backend-disconnected", wsListeners.value.onWsDisconnected);
  }
});
</script>

<style scoped>
.theme-border { border-color: var(--border-color) !important; }
.theme-bg-border { background-color: var(--border-color) !important; }

.settings-section {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  padding: 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.settings-section.\!p-0 {
  padding: 0 !important;
  gap: 0;
}
.settings-section-header {
  display: flex; align-items: center; gap: 0.75rem;
  padding-bottom: 1rem; border-bottom: 1px solid var(--border-color);
}
.settings-section-icon { padding: 0.5rem; background: rgba(var(--accent-rgb), 0.1); border-radius: 0.5rem; flex-shrink: 0; color: var(--accent); }
.settings-accordion-icon {
  width: 1.5rem; height: 1.5rem;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  color: var(--text-tertiary);
  opacity: 0.7;
}
.settings-section-title { font-size: 1rem; font-weight: 700; color: var(--text-primary); }
.settings-section-desc  { font-size: 0.75rem; color: var(--text-secondary); margin-top: 0.125rem; }

.settings-label {
  display: block; font-size: 0.7rem; font-weight: 700; color: var(--text-secondary);
  text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.375rem;
}
.settings-input {
  width: 100%; min-height: 2.5rem; padding: 0.5rem 0.875rem;
  background-color: var(--bg-primary); border: 1px solid var(--border-color) !important;
  border-radius: 0.5rem !important; font-size: 0.875rem; color: var(--text-primary);
  outline: none !important; box-shadow: none !important; transition: border-color 0.2s ease;
}
.settings-input:focus        { border-color: var(--accent) !important; }
.settings-input::placeholder { color: var(--text-secondary); opacity: 0.7; }

.settings-select {
  height: 2.5rem;
  padding: 0 2.25rem 0 0.75rem;
  background-color: var(--bg-secondary);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.6rem center;
  border: 1px solid var(--border-color); border-radius: 0.5rem;
  font-size: 0.8rem; color: var(--text-primary); outline: none; cursor: pointer;
  appearance: none; -webkit-appearance: none;
  transition: border-color 0.15s;
}
.settings-select:focus { border-color: var(--accent); }

.role-badge { display: inline-flex; align-items: center; padding: 0.2rem 0.6rem; border-radius: 9999px; font-size: 0.7rem; font-weight: 700; border: 1px solid; text-transform: uppercase; letter-spacing: 0.05em; }
.role-badge-sm    { font-size: 0.65rem; padding: 0.15rem 0.5rem; }
.role-badge-admin  { background: rgba(var(--accent-rgb),.1); border-color: rgba(var(--accent-rgb),.3); color: var(--accent); }
.role-badge-editor { background: rgba(245,158,11,.1); border-color: rgba(245,158,11,.3); color: #f59e0b; }
.role-badge-viewer { background: var(--bg-tertiary); border-color: var(--border-color); color: var(--text-secondary); }
.bg-success\/10 { background: rgba(34,197,94,.1); border-color: rgba(34,197,94,.3); color: #22c55e; }
.bg-warning\/10 { background: rgba(245,158,11,.1); border-color: rgba(245,158,11,.3); color: #f59e0b; }
.bg-error\/10   { background: rgba(239,68,68,.1); border-color: rgba(239,68,68,.3); color: #ef4444; }

.animate-spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.ws-btn {
  padding: 0.25rem 0.625rem;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 0.375rem;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s;
  white-space: nowrap;
  background: var(--bg-tertiary);
  color: var(--text-primary);
}
.ws-btn:hover          { opacity: 0.8; }
.ws-btn:disabled       { opacity: 0.4; cursor: not-allowed; }
.ws-btn-accent         { background: var(--accent); color: #fff; }
.ws-btn-danger         { background: rgba(239,68,68,.08); color: #ef4444; }

.ws-icon-btn {
  width: 1.75rem; height: 1.75rem;
  display: flex; align-items: center; justify-content: center;
  border-radius: 0.375rem; border: none; cursor: pointer;
  background: transparent;
  color: var(--text-secondary);
  transition: background 0.15s, color 0.15s;
}
.ws-icon-btn:hover           { background: var(--bg-quaternary); color: var(--text-primary); }
.ws-icon-btn-danger          { color: var(--text-secondary); }
.ws-icon-btn-danger:hover    { background: rgba(239,68,68,.1); color: #ef4444; }

.custom-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.1) transparent; }
.custom-scrollbar::-webkit-scrollbar       { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
</style>